import { createContext, use } from 'react';

import type { AuthUser } from '../types';

/**
 * État d'authentification de l'application.
 *
 * `status` est une machine à trois états plutôt qu'un booléen `isLoading` :
 * au premier rendu, on ne sait pas encore si l'utilisateur a une session
 * (le cookie de refresh doit d'abord être échangé). Sans l'état `loading`
 * explicite, une garde de route renverrait vers la connexion un utilisateur
 * pourtant authentifié — un aller-retour visible à chaque rafraîchissement.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Renseigne la session après une connexion réussie. */
  setSession: (accessToken: string, user: AuthUser) => void;
  /** Révoque la session côté serveur puis vide l'état local. */
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Accès à l'état d'authentification.
 *
 * Lève si le composant n'est pas sous `<AuthProvider>` : mieux vaut une erreur
 * immédiate et explicite qu'un `user` silencieusement `null` qui produirait un
 * bug d'affichage difficile à relier à sa cause.
 */
export const useAuth = (): AuthContextValue => {
  const context = use(AuthContext);

  if (!context) {
    throw new Error('useAuth doit être utilisé à l’intérieur de <AuthProvider>.');
  }

  return context;
};
